/**
 * @file This file contains the AudioManager class, responsible for loading,
 * playing, and managing audio in the game.
 * @module AudioManager
 */

/**
 * Manages all audio playback in the game. It handles loading sounds,
 * playing them, and muting/unmuting. It also deals with the browser's
 * requirement for user interaction before playing audio.
 * @class AudioManager
 */

export class AudioManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {object} dependencies.config - The main audio configuration object from audioConfig.js.
     * @param {object} dependencies.effectsConfig - The audio effects configuration from audioEffectsConfig.js.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     */
    constructor({ config, effectsConfig, eventEmitter }) {
        /** @private */
        this.config = config.sounds;
        /** @private */
        this.effectsConfig = effectsConfig;
        /** @private @type {Map<string, AudioBuffer>} */
        this.impulseResponses = new Map();
        /** @private */
        this.eventEmitter = eventEmitter;
        /** @private @type {Map<string, AudioBuffer>} */
        this.sounds = new Map();
        /** @private @type {boolean} */
        this.isMuted = false;
        /** @private @type {AudioContext|null} */
        this.audioContext = null;
        /** @private @type {boolean} */
        this.isUnlocked = false;
        /** @private @type {Array<{name: string, url: string}>} */
        this._loadQueue = [];
        /** @private @type {Array<string>} */
        this._impulseLoadQueue = [];
        /** @private @type {AudioBufferSourceNode|null} */
        this.nowPlayingForeground = null;
        /**
         * @private
         * @type {{source: AudioBufferSourceNode, gainNode: GainNode}|null}
         */
        this.nowPlayingBackground = null;
    }

    /**
     * Prepares the audio manager by queueing sounds from the config.
     */
    initManager() {
        if (this.config) {
            Object.entries(this.config).forEach(([name, soundData]) => {
                if (soundData.urls && Array.isArray(soundData.urls)) {
                    soundData.urls.forEach((url, index) => {
                        this.loadSound(`${name}_${index}`, url);
                    });
                } else if (soundData.url) {
                    this.loadSound(name, soundData.url);
                }
            });

            if (this.effectsConfig) {
                Object.values(this.effectsConfig).forEach(effect => {
                    if (effect.type === 'reverb' && effect.impulseResponse) {
                        this._loadImpulseResponse(effect.impulseResponse);
                    } else if (effect.type === 'chain') {
                        effect.nodes.forEach(node => {
                            if (node.type === 'reverb' && node.impulseResponse) this._loadImpulseResponse(node.impulseResponse);
                        });
                    }
                });
            }
        }
    }

    /**
     * Creates the AudioContext on the first user interaction.
     * This is required by modern browsers to play audio.
     */
    unlockAudio() {
        if (this.isUnlocked) return;
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.isUnlocked = true;
            this._processLoadQueue(); // Process any sounds queued before unlock.
            this._processImpulseLoadQueue(); // Process any impulse responses queued before unlock.
        } catch (e) {
            console.error('Could not create AudioContext:', e);
        }
    }

    /**
     * Loads a sound or queues it if the audio context is not yet available.
     * @param {string} name - The key to store the sound under.
     * @param {string} url - The path to the sound file.
     * @returns {Promise<void>} A promise that resolves when the sound is loaded.
     */
    async loadSound(name, url) {
        if (!this.isUnlocked) {
            this._loadQueue.push({ name, url });
            return;
        }
        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.sounds.set(name, audioBuffer);
        } catch (error) {
            console.error(`Failed to load sound: ${name} from ${url}`, error);
        }
    }

    /**
     * Loads an impulse response file for use in reverb effects.
     * @param {string} url - The URL of the impulse response audio file.
     * @private
     */
    async _loadImpulseResponse(url) {
        if (!this.isUnlocked) {
            this._impulseLoadQueue.push(url);
            return;
        }
        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.impulseResponses.set(url, audioBuffer);
        } catch (error) {
            console.error(`Failed to load impulse response: ${url}`, error);
        }
    }

   /**
     * Plays a sound by its registered name.
     * @param {string} name - The name of the sound to play.
     * @param {object} [options={}] - Optional parameters for playback.
     * @param {number|null} [options.x] - The x-coordinate (column) of the sound source for panning.
     * @param {number|null} [options.gridWidth] - The total width of the grid for panning calculation.
     */
    play(name, { x = null, gridWidth = null } = {}) {
        let soundConfig = this.config[name];
        let soundNameToPlay = name;

        if (this.isMuted || !this.isUnlocked || !soundConfig) {
            return;
        }

        // If the config has an array of URLs, pick one at random.
        if (soundConfig.urls && Array.isArray(soundConfig.urls)) {
            const randomIndex = Math.floor(Math.random() * soundConfig.urls.length);
            soundNameToPlay = `${name}_${randomIndex}`;
        }

        if (!this.sounds.has(soundNameToPlay)) return;

        const soundType = soundConfig.type || 'foreground';

        try {
            const source = this.audioContext.createBufferSource();
            source.buffer = this.sounds.get(soundNameToPlay);
            const effectKey = soundConfig.effect;

            let lastNode = source;

            // --- NEW: Stereo Panning Logic ---
            if (typeof x === 'number' && typeof gridWidth === 'number' && gridWidth > 1) {
                const panner = this.audioContext.createStereoPanner();
                // Map grid x-coordinate (0 to gridWidth-1) to pan value (-1 for left, 0 for center, 1 for right)
                const panValue = (x / (gridWidth - 1)) * 2 - 1;
                panner.pan.setValueAtTime(panValue, this.audioContext.currentTime);
                lastNode.connect(panner);
                lastNode = panner; // The panner is now the last node before effects/destination
            }

            // Create effect chain if specified
            if (effectKey && this.effectsConfig[effectKey]) {
                const effectChain = this._createEffectChain(effectKey);
                if (effectChain) {
                    lastNode.connect(effectChain.input);
                    lastNode = effectChain.output;
                }
            }

            if (soundType === 'background') {
                if (this.nowPlayingBackground) {
                    this.nowPlayingBackground.source.stop();
                    this.nowPlayingBackground.gainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
                }
                const gainNode = this.audioContext.createGain();
                lastNode.connect(gainNode);
                gainNode.connect(this.audioContext.destination);

                const duration = source.buffer.duration;
                const fadeOutSeconds = 2.0;
                const now = this.audioContext.currentTime;

                if (duration > fadeOutSeconds) {
                    const fadeOutStartTime = now + duration - fadeOutSeconds;
                    gainNode.gain.setValueAtTime(1, now);
                    gainNode.gain.linearRampToValueAtTime(1, fadeOutStartTime); // Hold volume until fade
                    gainNode.gain.linearRampToValueAtTime(0.0001, now + duration); // Then ramp down to near-zero
                }

                this.nowPlayingBackground = { source, gainNode };
                source.onended = () => {
                    if (this.nowPlayingBackground && this.nowPlayingBackground.source === source) {
                        this.nowPlayingBackground = null;
                        this.eventEmitter.emit('audio:soundEnded', { name, type: 'background' });

                        // If the track that just ended was part of the level music playlist, play the next one.
                        if (name === 'level_music') {
                            this.play('level_music');
                        }
                    }
                };
            } else { // 'foreground'
                if (this.nowPlayingForeground) {
                    this.nowPlayingForeground.stop();
                }
                lastNode.connect(this.audioContext.destination);
                this.nowPlayingForeground = source;
                source.onended = () => {
                    if (this.nowPlayingForeground === source) {
                        this.nowPlayingForeground = null;
                        this.eventEmitter.emit('audio:soundEnded', { name, type: 'foreground' });

                    }
                };
            }

            source.start(0);
        } catch (e) {
            console.error(`Error playing sound ${name}:`, e);
        }
    }

    /**
     * Creates a chain of audio nodes for a given effect key.
     * @param {string} effectKey - The key of the effect from the effects config.
     * @returns {{input: AudioNode, output: AudioNode}|null} An object with the first and last nodes of the chain.
     * @private
     */
    _createEffectChain(effectKey) {
        const effectConfig = this.effectsConfig[effectKey];
        if (!effectConfig) return null;

        let inputNode = null;
        let lastNode = null;

        const processNodeConfig = (nodeConfig) => {
            let currentNode = null;
            switch (nodeConfig.type) {
            case 'reverb':
                const impulse = this.impulseResponses.get(nodeConfig.impulseResponse);
                if (impulse) {
                    const convolver = this.audioContext.createConvolver();
                    convolver.buffer = impulse;
                    currentNode = convolver;
                }
                break;
            case 'filter':
                const filter = this.audioContext.createBiquadFilter();
                filter.type = nodeConfig.filterType || 'lowpass';
                filter.frequency.setValueAtTime(nodeConfig.frequency || 1000, this.audioContext.currentTime);
                filter.Q.setValueAtTime(nodeConfig.q || 1, this.audioContext.currentTime);
                currentNode = filter;
                break;
            }
            if (currentNode) {
                if (!inputNode) inputNode = currentNode;
                if (lastNode) lastNode.connect(currentNode);
                lastNode = currentNode;
            }
        };

        if (effectConfig.type === 'chain') {
            effectConfig.nodes.forEach(processNodeConfig);
        } else {
            processNodeConfig(effectConfig);
        }

        return inputNode ? { input: inputNode, output: lastNode } : null;
    }

    /**
     * Stops the currently playing sound on a specific channel.
     * @param {('foreground'|'background')} channel - The channel to stop.
     */
    stop(channel) {
        if (channel === 'background' && this.nowPlayingBackground) {
            this.nowPlayingBackground.source.stop();
            this.nowPlayingBackground.gainNode.gain.cancelScheduledValues(this.audioContext.currentTime);
            this.nowPlayingBackground = null;
        } else if (channel === 'foreground' && this.nowPlayingForeground) {
            this.nowPlayingForeground.stop();
            this.nowPlayingForeground = null;
        }
    }

    /**
     * Toggles the mute state of the audio manager.
     */
    toggleMute() {
        this.isMuted = !this.isMuted;
        if (this.isMuted) {
            this.stop('foreground');
            this.stop('background');
        }
        this.eventEmitter.emit('audio:muteStateChanged', { isMuted: this.isMuted });
    }

    /**
     * Gets the current mute state.
     * @returns {boolean} True if audio is muted, false otherwise.
     */
    getMuteState() {
        return this.isMuted;
    }

    /**
     * Processes any sounds that were queued for loading before the AudioContext was unlocked.
     * @private
     */
    _processLoadQueue() {
        this._loadQueue.forEach(item => this.loadSound(item.name, item.url));
        this._loadQueue = [];
    }

    /**
     * Processes any impulse responses that were queued for loading before the AudioContext was unlocked.
     * @private
     */
    _processImpulseLoadQueue() {
        this._impulseLoadQueue.forEach(url => this._loadImpulseResponse(url));
        this._impulseLoadQueue = [];
    }
}